/**
 * RocketLineupCard — a Team GO Rocket opponent's three slots (plan phase 4, decision 0019).
 *
 * The backend's ``rocket_lineup_card`` carries, per slot, the possible Pokémon
 * with their types and the attacking types that beat the slot, plus which of
 * them can be caught afterwards. Rendered by CardRenderer.
 */
import type { VisualPayload } from "../../types";
import CardShell from "./_CardShell";
import { TYPE_BADGE_COLORS } from "../../utils/typeColors";

interface Props { payload: VisualPayload }
interface Option { name: string; types?: string[]; is_encounter?: boolean; can_be_shiny?: boolean }
interface Slot { slot: number; options: Option[]; counters: string[] }

function TypeDot({ type }: { type: string }) {
  const cls = TYPE_BADGE_COLORS[type.toLowerCase()] ?? "bg-white/10 text-white/60";
  return <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${cls}`}>{type}</span>;
}

export default function RocketLineupCard({ payload }: Props) {
  const sec = payload.sections.find((s) => s.section === "lineup") as Record<string, unknown> | undefined;
  if (!sec) {
    return (
      <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined}>
        <p className="text-sm text-white/40 py-4 text-center">No line-up on file.</p>
      </CardShell>
    );
  }
  const slots = Array.isArray(sec.slots) ? (sec.slots as Slot[]) : [];
  const encounters = Array.isArray(sec.encounters) ? (sec.encounters as string[]) : [];
  return (
    <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined}>
      <div className="space-y-3">
        {typeof sec.title === "string" && <p className="text-[10px] text-white/30 uppercase tracking-wide">{sec.title}</p>}
        {slots.map((s) => (
          <div key={s.slot} className="rounded-xl border border-white/[0.07] p-3 space-y-2">
            <p className="text-xs font-semibold text-white/70">Slot {s.slot}</p>
            <div className="space-y-1">
              {s.options.map((o) => (
                <div key={o.name} className="flex items-center gap-2 text-sm">
                  <span className="text-white/90">{o.name}</span>
                  {(o.types ?? []).map((t) => <TypeDot key={t} type={t} />)}
                  {o.is_encounter && <span className="text-[10px] text-emerald-300/80">catchable{o.can_be_shiny ? " · shiny" : ""}</span>}
                </div>
              ))}
            </div>
            {s.counters.length > 0 && (
              <p className="text-xs text-white/60">Bring: {s.counters.join(", ")}</p>
            )}
          </div>
        ))}
        {encounters.length > 0 && <p className="text-[11px] text-white/45">Encounter after the win: {encounters.join(" · ")}</p>}
      </div>
    </CardShell>
  );
}
